import { useEffect, useState, useCallback } from 'react'
import { testimonialsAPI } from '@utils/api'
import AdminLayout from '@components/admin/AdminLayout'
import { TableSkeleton } from '@components/admin/AdminSkeleton'
import {
  Search, Star, CheckCircle2, XCircle, Loader2,
  MessageSquare, RefreshCw, ChevronLeft, ChevronRight,
  Eye, Trash2, Award
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

const STATUS_CLS = {
  pending:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100  text-green-700  dark:bg-green-900/30  dark:text-green-400',
  rejected: 'bg-red-100    text-red-700    dark:bg-red-900/30    dark:text-red-400',
}

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />
    ))}
  </div>
)

const AdminTestimonials = () => {
  const [items,   setItems]   = useState([])
  const [loading, setLoading] = useState(true)
  const [search,  setSearch]  = useState('')
  const [filter,  setFilter]  = useState('all')
  const [page,    setPage]    = useState(1)
  const [total,   setTotal]   = useState(0)
  const [acting,  setActing]  = useState({})
  const limit = 10

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, limit }
      if (filter !== 'all') params.status = filter
      if (search) params.search = search
      const res = await testimonialsAPI.getAll(params)
      setItems(res.data || [])
      setTotal(res.pagination?.total || res.data?.length || 0)
    } catch { toast.error('Failed to load testimonials') }
    finally { setLoading(false) }
  }, [page, filter, search])

  useEffect(() => { load() }, [load])

  const act = async (id, fn, label) => {
    setActing(a => ({ ...a, [id]: label }))
    try {
      await fn()
      toast.success(`Testimonial ${label}`)
      load()
    } catch { toast.error(`Failed to ${label}`) }
    finally { setActing(a => { const n = { ...a }; delete n[id]; return n }) }
  }

  const approve = (id) => act(id, () => testimonialsAPI.approve(id, { status: 'approved' }), 'approved')
  const reject  = (id) => act(id, () => testimonialsAPI.reject(id), 'rejected')
  const del     = (id) => act(id, () => testimonialsAPI.delete(id), 'deleted')
  const feature = (id) => act(id, () => testimonialsAPI.toggleFeatured(id), 'updated')

  const totalPages = Math.ceil(total / limit)

  return (
    <AdminLayout>
      <div className="space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">Testimonials</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review and manage student testimonials</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search testimonials…"
              className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 w-64" />
          </div>
          {['all','pending','approved','rejected'].map(s => (
            <button key={s} onClick={() => { setFilter(s); setPage(1) }}
              className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors capitalize
                ${filter === s
                  ? 'bg-primary-600 text-white'
                  : 'border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}>
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? <TableSkeleton rows={8} cols={5} /> : (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm font-medium">No testimonials found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40">
                      {['Student','Course','Rating','Status','Featured','Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800/60">
                    {items.map(t => (
                      <tr key={t._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{t.studentName}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{t.studentEmail}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <p className="text-gray-700 dark:text-gray-300 max-w-[180px] truncate">{t.course?.title || '—'}</p>
                        </td>
                        <td className="px-4 py-3.5">
                          <Stars n={t.rating} />
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_CLS[t.status] || ''}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <button onClick={() => feature(t._id)}
                            className={`p-1.5 rounded-lg transition-colors ${t.featured ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : 'text-gray-300 hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'}`}>
                            <Award className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1">
                            {t.status === 'pending' && (
                              <>
                                <button onClick={() => approve(t._id)} disabled={!!acting[t._id]}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-100 transition-colors disabled:opacity-50">
                                  {acting[t._id] === 'approved' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                  Approve
                                </button>
                                <button onClick={() => reject(t._id)} disabled={!!acting[t._id]}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50">
                                  {acting[t._id] === 'rejected' ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                  Reject
                                </button>
                              </>
                            )}
                            <button onClick={() => del(t._id)} disabled={!!acting[t._id]}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
                              {acting[t._id] === 'deleted' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-400">{total} total</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                  <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">{page} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminTestimonials
